/**
 * @file     transferTax.ts
 * @purpose  The one BC property transfer tax module: general bands, the
 *           residential surtax, the first-time buyer and newly built home
 *           exemptions, the additional tax on foreign buyers, and the combined
 *           estimate. Pure and client-safe, so the server cost service, the map
 *           estimator and the public calculator compute from one source.
 *           Statutory figures verified against gov.bc.ca on PTT_RULES_VERIFIED_ON.
 */

/** Date every figure in this file was last checked against www2.gov.bc.ca. */
export const PTT_RULES_VERIFIED_ON = '2026-10-06';

/**
 * General rate bands.
 * Source: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax
 */
export const PTT_BANDS: ReadonlyArray<{ upTo: number | null; rate: number }> = [
  { upTo: 200_000, rate: 0.01 },
  { upTo: 2_000_000, rate: 0.02 },
  { upTo: null, rate: 0.03 },
];

/** A further 2% applies to residential value above $3,000,000 (same source). */
export const PTT_RESIDENTIAL_SURTAX_FROM = 3_000_000;
export const PTT_RESIDENTIAL_SURTAX_RATE = 0.02;

/**
 * First Time Home Buyers' Program, thresholds effective 2024-04-01: the tax
 * on the first $500,000 is exempt up to $835,000, then phases out in a
 * straight line to $860,000.
 * Source: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax/exemptions/first-time-home-buyers
 * Table: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax/exemptions/first-time-home-buyers/current-amount
 */
export const FTHB_EXEMPT_PORTION = 500_000;
export const FTHB_PARTIAL_FROM = 835_000;
export const FTHB_PARTIAL_TO = 860_000;

/**
 * Newly built home exemption, thresholds effective 2024-04-01 and unchanged
 * as of 2026: the whole tax is exempt up to $1,100,000, then
 * PTT x ((1,100,000 + 50,000 - FMV) / 50,000) up to $1,150,000.
 * Source: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax/exemptions/newly-built-home-exemption
 * Formula and table: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax/exemptions/newly-built-home-exemption/exemption-amounts
 */
export const NEWLY_BUILT_FULL_TO = 1_100_000;
export const NEWLY_BUILT_PARTIAL_TO = 1_150_000;

/**
 * Additional property transfer tax: 20% of the fair market value of a foreign
 * entity's or taxable trustee's share of residential property in these areas.
 * Source: https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax/additional-property-transfer-tax
 */
export const ADDITIONAL_PTT_RATE = 0.2;
export const ADDITIONAL_PTT_REGIONS = [
  'Capital Regional District',
  'Fraser Valley Regional District',
  'Metro Vancouver Regional District',
  'Regional District of Central Okanagan',
  'Regional District of Nanaimo',
] as const;

const validPrice = (price: number): number => (Number.isFinite(price) && price > 0 ? price : 0);

/** Property transfer tax on a residential purchase, before any exemption. */
export function propertyTransferTax(price: number): number {
  let tax = 0;
  let floor = 0;
  for (const band of PTT_BANDS) {
    const ceiling = band.upTo ?? Infinity;
    if (price <= floor) break;
    tax += (Math.min(price, ceiling) - floor) * band.rate;
    floor = ceiling;
  }
  if (price > PTT_RESIDENTIAL_SURTAX_FROM) {
    tax += (price - PTT_RESIDENTIAL_SURTAX_FROM) * PTT_RESIDENTIAL_SURTAX_RATE;
  }
  return Math.round(tax);
}

/**
 * The first-time buyer exemption. Full exemption applies to the tax on the
 * first $500,000 of value; above $835,000 it phases out, and at $860,000 it is
 * gone. Returns the amount of tax removed, never more than the tax itself.
 */
export function firstTimeBuyerRelief(price: number): number {
  if (price >= FTHB_PARTIAL_TO) return 0;
  const full = Math.min(propertyTransferTax(Math.min(price, FTHB_EXEMPT_PORTION)), propertyTransferTax(price));
  if (price <= FTHB_PARTIAL_FROM) return full;
  const remaining = (FTHB_PARTIAL_TO - price) / (FTHB_PARTIAL_TO - FTHB_PARTIAL_FROM);
  return Math.round(full * remaining);
}

/** Tax removed by the newly built home exemption. */
export function newlyBuiltHomeRelief(price: number): number {
  const p = validPrice(price);
  if (p === 0 || p >= NEWLY_BUILT_PARTIAL_TO) return 0;
  const full = propertyTransferTax(p);
  if (p <= NEWLY_BUILT_FULL_TO) return full;
  const remaining = (NEWLY_BUILT_PARTIAL_TO - p) / (NEWLY_BUILT_PARTIAL_TO - NEWLY_BUILT_FULL_TO);
  return Math.round(full * remaining);
}

/** The additional 20% tax on a foreign buyer's full share of a residential purchase. */
export function additionalPropertyTransferTax(price: number): number {
  return Math.round(validPrice(price) * ADDITIONAL_PTT_RATE);
}

export type PttExemption = 'first-time' | 'newly-built' | null;

export interface PttInputs {
  price: number;
  firstTimeBuyer?: boolean;
  newlyBuilt?: boolean;
  /** Foreign national, foreign corporation or taxable trustee in a designated region. */
  foreignBuyer?: boolean;
}

export interface PttEstimate {
  price: number;
  /** General tax before any exemption. */
  generalTax: number;
  firstTimeRelief: number;
  newlyBuiltRelief: number;
  /** The exemption that was applied, when more than one could. */
  exemptionApplied: PttExemption;
  relief: number;
  taxAfterExemption: number;
  additionalTax: number;
  /** Everything payable at registration, with the exemption applied. */
  total: number;
  /** The same total if no exemption applied. */
  totalWithoutExemption: number;
}

/**
 * One purchase cannot claim both exemptions on the same tax, so where both
 * apply the larger relief is used. Both exemptions require a Canadian citizen
 * or permanent resident, so a foreign buyer gets neither.
 */
export function estimatePropertyTransferTax(input: PttInputs): PttEstimate {
  const price = validPrice(input.price);
  const generalTax = propertyTransferTax(price);
  const canClaim = !input.foreignBuyer;
  const firstTimeRelief = canClaim && input.firstTimeBuyer ? firstTimeBuyerRelief(price) : 0;
  const newlyBuiltRelief = canClaim && input.newlyBuilt ? newlyBuiltHomeRelief(price) : 0;

  let exemptionApplied: PttExemption = null;
  let relief = 0;
  if (newlyBuiltRelief > 0 && newlyBuiltRelief >= firstTimeRelief) {
    exemptionApplied = 'newly-built';
    relief = newlyBuiltRelief;
  } else if (firstTimeRelief > 0) {
    exemptionApplied = 'first-time';
    relief = firstTimeRelief;
  }

  const taxAfterExemption = Math.max(generalTax - relief, 0);
  const additionalTax = input.foreignBuyer ? additionalPropertyTransferTax(price) : 0;

  return {
    price,
    generalTax,
    firstTimeRelief,
    newlyBuiltRelief,
    exemptionApplied,
    relief,
    taxAfterExemption,
    additionalTax,
    total: taxAfterExemption + additionalTax,
    totalWithoutExemption: generalTax + additionalTax,
  };
}
